import { useQueries } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'

import { Currency } from '@/constants/currency'
import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import {
  getBankEligibility,
  getBankEligibilityOnboardingState,
} from '@/domains/Business/api'
import {
  useBusinessRole,
  useFeature,
  useWallets,
} from '@/domains/Business/hooks'
import {
  BankEligibility,
  BankEligibilityDocumentState,
  BankEligibilityOnboardingState,
  Feature,
} from '@/domains/Business/types'
import { getImage } from '@/lib/images'
import { cn } from '@/lib/utils'
import { ChevronRight } from '@/shared/icons/outline'
import { Button, Card, MotionDiv, Typography } from '@/shared/ui'

import { getAccountDetails } from '../api'
import { AccountDetails, AccountType } from '../types'

type Props = {
  bankEligibility?: BankEligibility
  className?: string
}

export const EURCBanner = ({ className }: Props) => {
  const { isAdmin } = useBusinessRole()
  const { detailsPerCurrency } = useWallets()

  const { isEnabled: isEURCWalletEnabled, isPending: isEURCWalletPending } =
    useFeature(Feature.EURC_WALLET)

  const mainEURCWallet = detailsPerCurrency?.find(
    (detail) => detail.currency === Currency.EURC,
  )?.mainWalletId

  const [
    bankEligibilityQuery,
    accountDetailsQuery,
    bankEligibilityOnboardingStateQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: [queryKeys.getBankEligibility],
        queryFn: () => getBankEligibility(),
        select: (data: AxiosResponse<BankEligibility>) => data.data,
        enabled: isAdmin,
      },
      {
        queryKey: [queryKeys.getAccountDetails, mainEURCWallet],
        queryFn: () => getAccountDetails({ id: mainEURCWallet ?? '' }),
        select: (data: AxiosResponse<AccountDetails>) => data?.data,
        enabled: !!mainEURCWallet,
      },
      {
        queryKey: [queryKeys.getBankEligibilityOnboardingState],
        queryFn: () => getBankEligibilityOnboardingState(AccountType.EU_LOCAL),
        select: (data: AxiosResponse<BankEligibilityOnboardingState>) =>
          data.data,
        enabled: isAdmin && isEURCWalletEnabled,
        retry: 1,
      },
    ],
  })

  const hasEURCBankAccountDetails = accountDetailsQuery.data?.bankAccounts.some(
    (account) => account.type === AccountType.EU_LOCAL,
  )

  if (
    !isEURCWalletEnabled ||
    isEURCWalletPending ||
    bankEligibilityQuery.isFetching ||
    accountDetailsQuery.isFetching ||
    bankEligibilityOnboardingStateQuery.isFetching ||
    bankEligibilityQuery.data?.currentStepDetails.state ===
      BankEligibilityDocumentState.NOT_STARTED ||
    hasEURCBankAccountDetails
  ) {
    return null
  }

  const getBannerContent = () => {
    if (bankEligibilityOnboardingStateQuery.data?.state) {
      return (
        <div className="flex items-center gap-3 pr-2">
          <img
            src={getImage({ name: 'docs-approved' })}
            alt=""
            aria-hidden
            className="size-16 object-contain"
          />
          <div className="flex flex-col gap-1">
            <Typography bold>
              <FormattedMessage
                id="accounts.eurc.banner.completed.title"
                defaultMessage="We received your EURO application!"
              />
            </Typography>
            <Typography variant="body-small">
              <FormattedMessage
                id="accounts.eurc.banner.completed.description"
                defaultMessage="We will notify you as soon as the review is done or if we need any other info"
              />
            </Typography>
          </div>
        </div>
      )
    }

    switch (bankEligibilityQuery.data?.currentStepDetails.state) {
      case BankEligibilityDocumentState.IN_PROGRESS:
      case BankEligibilityDocumentState.COMPLETED:
        return (
          <>
            <div className="flex items-center gap-3 pr-2">
              <img
                src={getImage({ name: 'docs-pending' })}
                alt=""
                aria-hidden
                className="size-16 object-contain"
              />
              <div className="flex flex-col gap-1">
                <Typography bold>
                  <FormattedMessage
                    id="accounts.eurc.banner.inProgress.title"
                    defaultMessage="You've almost finished applying for EURO payments!"
                  />
                </Typography>
                <Typography variant="body-small">
                  <FormattedMessage
                    id="accounts.eurc.banner.inProgress.description"
                    defaultMessage="We just need some more information, will take a minute or two"
                  />
                </Typography>
              </div>
            </div>
            <Button rightIcon={<ChevronRight />} asChild size="sm">
              <Link to={BusinessRoute.KYB}>
                <FormattedMessage id="action.finish" defaultMessage="Finish" />
              </Link>
            </Button>
          </>
        )

      default:
        return null
    }
  }

  return (
    <MotionDiv className={cn('', className)}>
      <Card size="widget">
        <div className="flex items-center justify-between">
          {getBannerContent()}
        </div>
      </Card>
    </MotionDiv>
  )
}
